import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'

import type { MessageChannel, RentalWritableChannel } from '@/types'
import {
	rentalMessagesApi,
	useListDealMessagesQuery,
	useListRentalMessagesQuery,
	useMarkDealMessageReadMutation,
	useSendDealMessageMutation,
	useSendRentalMessageMutation,
} from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import { useRealtimeDeal } from '@/lib/use-realtime-deal'

import { fromDealMessage, fromRentalMessage, type ConversationMessage } from './model'

/**
 * Channels a message can actually leave the system on.
 *
 * The same set crm-service dispatches through its outbox (EXTERNAL_CHANNELS in
 * outbound-message.service). WhatsApp and email are absent because nothing
 * delivers them: the row is written, stamped SENT and never sent, which is
 * worse than refusing the message — the landlord believes the tenant has it.
 * There are already rows in the database in exactly that state.
 *
 * Delivery is always resolved from the deal, which is what holds the external
 * chat binding, so a rental with no deal has nothing to write on either.
 */
const DELIVERABLE_CHANNELS: readonly MessageChannel[] = ['AVITO', 'CIAN', 'TELEGRAM']

/**
 * A rental with no deal has no external chat binding, so nothing can be
 * DELIVERED — but the manual conversation log (issue #261) still records
 * Telegram/WhatsApp exchanges conducted outside the product. Removing the
 * panels must not remove that: renewals and re-lets are created without a
 * deal by design, and their chat would otherwise be a dead composer.
 */
export const MANUAL_LOG_CHANNELS: readonly RentalWritableChannel[] = ['TELEGRAM', 'WHATSAPP']

const DEAL_PAGE_LIMIT = 200

export interface UseConversationArgs {
	/** The deal this conversation belongs to, if there is one. */
	dealId?: string
	/** Set on a rental: its thread is its own messages merged with the deal's. */
	rentalId?: string
}

export interface Conversation {
	messages: ConversationMessage[]
	isSending: boolean
	/** True when the last send failed; cleared by the next attempt. */
	sendFailed: boolean
	/** The server's reason, when it gave a readable one. */
	sendError: string | null
	/** False where the channel exists in the thread but cannot be written to. */
	canWrite: (channel: MessageChannel) => boolean
	/** Resolves false on failure so the composer keeps the typed text. */
	send: (channel: MessageChannel, content: string) => Promise<boolean>
	/**
	 * True when writes go to the rental's manual log (no deal, nothing is
	 * delivered anywhere) — the composer words itself accordingly.
	 */
	isManualLog: boolean
	/** True when older deal history exists beyond what is loaded. */
	hasOlder: boolean
	/** Fetches the next older page into the thread. */
	loadOlder: () => void
}

/**
 * The single source the chat panel reads, whichever page it is on.
 *
 * Both queries are always issued and one of them skipped, because which store
 * backs the thread is a prop and hooks cannot be called conditionally.
 *
 * Neither query filters by channel. The tabs are built from what the thread
 * actually contains, and a server-side filter cannot answer "which channels
 * exist" — asking per channel to find out would be one request per tab to
 * render the tab bar.
 */
export function useConversation({ dealId, rentalId }: UseConversationArgs): Conversation {
	const dispatch = useDispatch()

	// Grows by one page each time the user asks for older history. Truncating
	// at a fixed page silently, as the first version did, made a quiet
	// channel's tab disappear entirely once a busy one pushed its rows past
	// the limit. Reset during render on conversation change — the key
	// comparison keeps one conversation's depth from leaking into the next.
	const [pageCount, setPageCount] = useState(1)
	const conversationKey = dealId ?? rentalId
	const [prevKey, setPrevKey] = useState(conversationKey)
	if (prevKey !== conversationKey) {
		setPrevKey(conversationKey)
		setPageCount(1)
	}

	const { data: rentalData } = useListRentalMessagesQuery(
		{ rentalId: rentalId ?? '' },
		{ skip: !rentalId },
	)
	const { data: dealData } = useListDealMessagesQuery(
		// One page for the whole thread, not per channel. The server's default of
		// 50 was a page per channel while the query filtered by one; unfiltered,
		// 50 across four channels can leave a quiet tab looking empty next to a
		// busy one.
		{ dealId: dealId ?? '', limit: DEAL_PAGE_LIMIT * pageCount },
		{ skip: !!rentalId || !dealId },
	)

	const [sendDeal, { isLoading: isSendingDeal }] = useSendDealMessageMutation()
	const [sendRental, { isLoading: isSendingRental }] = useSendRentalMessageMutation()
	const isSending = isSendingDeal || isSendingRental
	const [markRead] = useMarkDealMessageReadMutation()
	const [sendFailed, setSendFailed] = useState(false)
	const [sendError, setSendError] = useState<string | null>(null)

	useRealtimeDeal(dealId, rentalId)

	const messages = useMemo<ConversationMessage[]>(() => {
		const rows = rentalId
			? (rentalData?.messages ?? [])
					.map(fromRentalMessage)
					.filter((m): m is ConversationMessage => m !== null)
			: (dealData?.items ?? []).map(fromDealMessage)
		// Oldest first, whichever endpoint served it. The deal list comes back
		// `sentAt: desc` and the rental's ascending; with one renderer for both
		// that difference would read as the deal's thread running backwards
		// inside each day. The id tiebreaker matters because the sort is
		// stable: equal timestamps would otherwise keep the server's
		// descending order and render an answer above its question.
		return [...rows].sort(
			(a, b) => a.sentAt.localeCompare(b.sentAt) || a.id.localeCompare(b.id),
		)
	}, [rentalId, rentalData, dealData])

	// Read receipts exist only on deal messages. The rental's merged view now
	// carries their read state too (an inherited row's id IS the deal message
	// id), so reading the thread on EITHER page acknowledges it — before this
	// the deal's unread badge stayed lit forever once the deal converted,
	// because the landlord had no reason to open the deal page again.
	useEffect(() => {
		if (!dealId) return
		const unread = messages.filter(
			(m) => m.direction === 'INBOUND' && m.isRead === false,
		)
		if (unread.length === 0) return
		void Promise.allSettled(
			unread.map((m) => markRead({ messageId: m.id, dealId }).unwrap()),
		).then(() => {
			// The merged list is served by the rental endpoint; without this
			// its cached rows keep isRead=false and the effect re-fires the
			// same acknowledgements on every refetch.
			if (rentalId) {
				dispatch(
					rentalMessagesApi.util.invalidateTags([
						{ type: 'RentalMessage', id: rentalId },
					]),
				)
			}
		})
	}, [messages, dealId, rentalId, markRead, dispatch])

	const canWrite = useCallback(
		(channel: MessageChannel) =>
			dealId
				? DELIVERABLE_CHANNELS.includes(channel)
				: !!rentalId &&
					MANUAL_LOG_CHANNELS.includes(channel as RentalWritableChannel),
		[dealId, rentalId],
	)

	const send = useCallback(
		async (channel: MessageChannel, content: string): Promise<boolean> => {
			const text = content.trim()
			if (!text || !canWrite(channel)) return false
			setSendFailed(false)
			setSendError(null)

			try {
				if (dealId) {
					// Always through the deal when there is one, including from
					// the rental page. The rental used to record Telegram and
					// WhatsApp in its own table, where nothing dispatched them
					// and the deal card could not see them — one person, one
					// thread, split across two stores.
					//
					// idempotencyKey guards against a double send on remount or
					// reconnect: the server returns the existing message instead
					// of posting a second one to Avito.
					await sendDeal({
						dealId,
						channel,
						content: text,
						idempotencyKey: uuidv4(),
					}).unwrap()
					// A rental reads the merged list off its own endpoint, so a
					// send through the deal has to invalidate that cache too —
					// RTK Query tags do not cross createApi instances.
					if (rentalId) {
						dispatch(
							rentalMessagesApi.util.invalidateTags([
								{ type: 'RentalMessage', id: rentalId },
							]),
						)
					}
				} else if (rentalId) {
					// No deal — the manual log. Recorded, not delivered; the
					// composer's placeholder says so.
					await sendRental({
						rentalId,
						channel: channel as RentalWritableChannel,
						content: text,
					}).unwrap()
				}
				return true
			} catch (err) {
				// Surfaced rather than swallowed: a send that fails silently
				// looks identical to one that worked, and the composer clears
				// either way.
				setSendFailed(true)
				setSendError(extractErrorMessage(err) ?? null)
				return false
			}
		},
		[canWrite, dealId, rentalId, sendDeal, sendRental, dispatch],
	)

	return {
		messages,
		isSending,
		sendFailed,
		sendError,
		canWrite,
		send,
		isManualLog: !dealId && !!rentalId,
		hasOlder:
			!rentalId && (dealData?.total ?? 0) > (dealData?.items?.length ?? 0),
		loadOlder: () => setPageCount((c) => c + 1),
	}
}
